import { post } from './api';

// 用户类型定义
export interface User {
  id: number;
  username: string;
  email: string;
  user_type: 'admin' | 'client' | 'sub';
  status: 'active' | 'disabled';
  client_id: number;
  parent_id?: number;
  created_at: string;
  updated_at: string;
}

// 用户列表查询参数
export interface UserListParams {
  page?: number;
  size?: number;
  user_type?: 'admin' | 'client' | 'sub';
  status?: 'active' | 'disabled';
  client_id?: number;
  keyword?: string;
}

// 用户列表响应
export interface UserListResponse {
  list: User[];
  total: number;
  page: number;
  size: number;
}

// 创建用户请求
export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  user_type: 'client' | 'sub';
  parent_id?: number;
  client_id?: number;
}

// 更新用户请求
export interface UpdateUserRequest {
  id: number;
  username?: string;
  email?: string;
  status?: 'active' | 'disabled';
}

// 子账号列表查询参数
export interface SubAccountListParams {
  page?: number;
  size?: number;
  status?: 'active' | 'disabled';
  keyword?: string;
}

// 创建子账号请求
export interface CreateSubAccountRequest {
  username: string;
  email: string;
  password: string;
  client_id: number;
}

// 更新子账号请求
export interface UpdateSubAccountRequest {
  id: number;
  username?: string;
  email?: string;
  status?: 'active' | 'disabled';
}

// 删除子账号请求
export interface DeleteSubAccountRequest {
  id: number;
}

// 切换用户状态请求
export interface ToggleUserStatusRequest {
  user_id: number;
}

// 修改密码请求
export interface ChangePasswordRequest {
  user_id: number;
  old_password: string;
  new_password: string;
}

// 用户管理API
export const userApi = {
  // 获取用户列表（系统管理员）
  getUsers: (params: UserListParams) => 
    post<UserListResponse>('/api/admin/users/list', params),

  // 创建用户（系统管理员）
  createUser: (data: CreateUserRequest) => 
    post<{ message: string; user: User }>('/api/admin/users/create', data),

  // 获取用户详情（系统管理员）
  getUserDetail: (userId: number) => 
    post<User>('/api/admin/users/detail', { user_id: userId }),

  // 更新用户信息（系统管理员）
  updateUser: (data: UpdateUserRequest) => 
    post<{ message: string }>('/api/admin/users/update', data),

  // 启用/禁用用户（系统管理员）
  toggleUser: (userId: number) => 
    post<{ message: string }>('/api/admin/users/toggle', { user_id: userId }),

  // 获取客户用户列表（系统管理员）
  getClientUsers: (params: UserListParams) => 
    post<UserListResponse>('/api/client/list', params),

  // 创建客户账号（系统管理员）
  createClient: (data: CreateUserRequest) => 
    post<{ message: string; user: User }>('/api/client/create', data),

  // 获取客户详情（系统管理员）
  getClientDetail: (userId: number) => 
    post<User>('/api/client/detail', { user_id: userId }),

  // 更新客户信息（系统管理员）
  updateClient: (data: UpdateUserRequest) => 
    post<{ message: string }>('/api/client/update', data),

  // 启用/禁用客户（系统管理员）
  toggleClient: (userId: number) => 
    post<{ message: string }>('/api/client/toggle', { user_id: userId }),

  // 切换客户指纹识别开关（系统管理员）
  toggleFingerprint: (userId: number) =>
    post<{ fingerprint_enabled: boolean }>('/api/admin/clients/toggle-fingerprint', { id: userId }),

  // 获取客户的子账号列表（系统管理员）
  getClientSubAccounts: (clientId: number, params?: SubAccountListParams) => 
    post<UserListResponse>('/api/admin/clients/sub-accounts', { client_id: clientId, ...params }),

  // 获取客户资料
  getClientProfile: () => 
    post<User>('/api/client/profile'),

  // 更新客户资料
  updateClientProfile: (data: UpdateUserRequest) => 
    post<{ message: string }>('/api/client/profile/update', data),

  // 获取子账号列表（客户账号）
  getSubAccountList: (params?: SubAccountListParams) => 
    post<UserListResponse>('/api/client/sub-accounts/list', params),

  // 创建子账号（客户账号）
  createSubAccount: (data: CreateSubAccountRequest) => 
    post<{ message: string; user: User }>('/api/client/sub-accounts/create', data),

  // 获取子账号详情（客户账号）
  getSubAccountDetail: (userId: number) => 
    post<User>('/api/client/sub-accounts/detail', { user_id: userId }),

  // 更新子账号信息（客户账号）
  updateSubAccount: (data: UpdateSubAccountRequest) => 
    post<{ message: string }>('/api/client/sub-accounts/update', data),

  // 删除子账号（客户账号）
  deleteSubAccount: (userId: number) => 
    post<{ message: string }>('/api/client/sub-accounts/delete', { user_id: userId }),

  // 启用/禁用子账号（客户账号）
  toggleSubAccount: (userId: number) => 
    post<{ message: string }>('/api/client/sub-accounts/toggle', { user_id: userId }),

  // 获取用户资料（通用）
  getUserProfile: () => 
    post<User>('/api/users/profile'),

  // 更新用户资料（通用）
  updateUserProfile: (data: UpdateUserRequest) => 
    post<{ message: string }>('/api/users/profile/update', data),

  // 修改密码
  changePassword: (data: ChangePasswordRequest) => 
    post<{ message: string }>('/api/users/change-password', data),
}; 